import platform from 'platform';
import { UAParser } from 'ua-parser-js';
import { IVideoPlatform } from '../interfaces/videoInterface';

export const getUAParserData = (userAgent:string):IVideoPlatform | undefined => {
  if (userAgent) {
    const uaParserData = new UAParser(userAgent);
    const Parser = uaParserData.getResult();
    const { ua } = Parser;
    const browserName = Parser.browser.name;
    const browserVersion = Parser.browser.version;
    const osName = Parser.os.name;
    const osVersion = Parser.os.version;
    const hints = Parser.withFeatureCheck?.();
    return {
      description: `${browserName} ${browserVersion} on ${osName} ${osVersion}`,
      layout: Parser?.engine?.name,
      manufacturer: null,
      name: browserName,
      prerelease: null,
      device: hints.device,
      browser: hints.browser,
      ua: ua.replace(/Macintosh;/, 'Linux;').replace(/Intel Mac OS X (\d+_\d+_\d+)/, 'Android $1').replace(/ AppleWebKit/, ' Mobile AppleWebKit'),
      version: browserVersion,
      os: {
        architecture: 32,
        family: osName,
        version: osVersion,
      },
    };
  }
  return undefined;
};

export const isMobile = ['Android', 'iOS'].some((os) => platform.os?.family?.includes(os))
|| platform.name?.includes('Mobile')
|| (navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform)); // For iPads

export const isAndroid = platform.os?.family === 'Android';
export const isIphone = platform.os?.family === 'iOS';
export const isFireFox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
export const isSupportedIosDevice = (platform.os?.family !== 'iOS' || Number((platform.os?.version || '14.4').split('.').slice(0, 2).join('.')) >= 14.4);
export const isSupportedDevice = 'MediaRecorder' in window && isSupportedIosDevice;

const PLATFORMS_OPTIONS = ['ANDROID', 'IOS', 'MACBOOK', 'WINDOWS'] as const;
const BROWSERS_OPTIONS = ['CHROME', 'SAFARI', 'FIREFOX', 'SAMSUNG'] as const;

type DEVICE_TYPE = {
  PLATFORM: typeof PLATFORMS_OPTIONS[number],
  BROWSER: typeof BROWSERS_OPTIONS[number]
};

export const DEVICE: DEVICE_TYPE = {
  PLATFORM: PLATFORMS_OPTIONS.find((p) => platform.os?.family?.toUpperCase().includes(p === 'MACBOOK' ? 'OS X' : p)) || 'WINDOWS',
  BROWSER: BROWSERS_OPTIONS.find((b) => platform.name?.toUpperCase().includes(b)) || 'CHROME',
};

const isPortrait = () => {
  if (typeof window.matchMedia !== 'undefined') {
    return window.matchMedia('(orientation: portrait)').matches;
  }
  return window.innerHeight > window.innerWidth;
};

const iPhoneModels = [
  { pixelRatio: 2, width: 640, height: 1136, model: 'iPhone SE (1st generation)' },
  { pixelRatio: 2, width: 750, height: 1334, model: 'iPhone SE (2nd generation)/6/6S/7/8' },
  { pixelRatio: 2, width: 828, height: 1792, model: 'iPhone XR' },
  { pixelRatio: 3, width: 1080, height: 1920, model: 'iPhone 6 Plus/6S Plus/7 Plus/8 Plus' },
  { pixelRatio: 3, width: 1125, height: 2436, model: 'iPhone X/XS/11 Pro' },
  { pixelRatio: 3, width: 1242, height: 2688, model: 'iPhone XS Max/11 Pro Max' },
  { pixelRatio: 3, width: 1170, height: 2532, model: 'iPhone 12/12 Pro/13/13 Pro/iPhone 14/iPhone 14 Pro' },
  { pixelRatio: 3, width: 1080, height: 2340, model: 'iPhone 12 Mini/13 Mini/iPhone 14 Mini' },
  { pixelRatio: 3, width: 1284, height: 2778, model: 'iPhone 12 Pro Max/13 Pro Max/iPhone 14 Pro Max' },
];
const matchedModelMap = new Map(iPhoneModels
  .map(({ pixelRatio, width, height, model }) => [[pixelRatio, width, height].join(), model]));

export const getIPhoneModel = () => {
  const { screen } = window;
  const screenWidth = (isPortrait() ? screen.width : screen.height) * window.devicePixelRatio;
  const screenHeight = (isPortrait() ? screen.height : screen.width) * window.devicePixelRatio;

  return matchedModelMap.get([devicePixelRatio, screenWidth, screenHeight].join()) || `${screenWidth}'x'${screenHeight} Unknown iPhone model`;
};
